import { cn } from "@/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
import { Calendar, ChevronDown } from "lucide-react";
import { type ComponentProps, forwardRef } from "react";

const triggerStyles = cva(
  [
    "group peer flex appearance-none items-center gap-x-2 truncate rounded-md border px-3 h-10 outline-none text-sm",
    "bg-background border-border text-foreground placeholder:text-muted-foreground/70",
    "cursor-pointer disabled:cursor-not-allowed",
    "disabled:bg-muted disabled:text-muted-foreground/70",
    "focus-visible:border-input data-[state=open]:border-input data-[state=open]:ring-4 data-[state=open]:ring-ring",
  ],
  {
    variants: {
      hasError: {
        true: "ring-2 ring-destructive/30 border-destructive/50",
      },
    },
  },
);

interface TriggerProps extends ComponentProps<"button">, VariantProps<typeof triggerStyles> {
  placeholder?: string;
}

export const Trigger = forwardRef<HTMLButtonElement, TriggerProps>(
  ({ className, children, placeholder, hasError, disabled, ...props }, ref) => {
    return (
      <button
        ref={ref}
        type="button"
        className={cn(triggerStyles({ hasError }), className)}
        disabled={disabled}
        {...props}
      >
        <Calendar className="size-4 shrink-0" />
        <span className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-left">
          {children ? (
            children
          ) : placeholder ? (
            <span className="text-muted-foreground/70">{placeholder}</span>
          ) : null}
        </span>
        <ChevronDown className="size-4 flex-shrink-0 text-muted-foreground/70 transition-transform duration-75 group-data-[state=open]:rotate-180" />
      </button>
    );
  },
);

Trigger.displayName = "DatePicker.Trigger";
